import { initProductionTabs } from "./production-tabs";
import { initProductionOverlayImages } from "./production-overlay-images";
import { initBlurUp } from "./blur-up";
import { initProjectTitlePan } from "./project-title-pan";
import { initProductionCarousel } from "./production-carousel";

const fetchOverlay = async (url) => {
  const response = await fetch(url, {
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load ${url}`);
  }
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector(".c-production-overlay");
};

const TRANSITION_MS = 600;

const setModalState = (modal, isOpen, skipAnimation = false) => {
  const content = modal.querySelector(".c-production-modal__content");
  
  if (isOpen) {
    // If modal is already open (navigating between productions), skip animation
    if (skipAnimation || modal.classList.contains("is-visible")) {
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-modal-open");
      modal.classList.add("is-visible");
      modal.classList.remove("is-opening");
      modal.classList.remove("is-closing");
      return;
    }
    
    // Reset state
    modal.classList.remove("is-closing");
    modal.classList.remove("is-visible");
    
    // Show modal (content is off-screen due to CSS default translateY(100%))
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("is-modal-open");
    
    // Safari-compatible: force style recalculation
    if (content) {
      window.getComputedStyle(content).transform;
    }
    
    // Longer timeout for Safari compatibility
    setTimeout(() => {
      modal.classList.add("is-visible");
      modal.classList.add("is-opening");
    }, 50);
    
    setTimeout(() => {
      modal.classList.remove("is-opening");
    }, TRANSITION_MS + 100);
    
  } else {
    modal.classList.remove("is-opening");
    modal.classList.add("is-closing");
    modal.classList.remove("is-visible");
    document.body.classList.remove("is-modal-open");
    
    setTimeout(() => {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      modal.classList.remove("is-closing");
    }, TRANSITION_MS);
  }
};

const bindModalEvents = (modal, onNavigate) => {
  modal.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest("[data-close], .c-production__control--close")) {
      event.preventDefault();
      setModalState(modal, false);
      return;
    }
    const prevLink = target.closest(".c-production__control--prev");
    if (prevLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      onNavigate(prevLink.href);
    }
    const nextLink = target.closest(".c-production__control--next");
    if (nextLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      onNavigate(nextLink.href);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !modal.hidden) {
      setModalState(modal, false);
    }
  });
};

export const initProductionOverlay = () => {
  const modal = document.querySelector(".c-production-modal");
  if (!modal) {
    return;
  }
  const content = modal.querySelector(".c-production-modal__content");
  if (!content) {
    return;
  }

  let destroyCarousel = null;

  const openModal = async (url) => {
    try {
      if (destroyCarousel) {
        destroyCarousel();
        destroyCarousel = null;
      }
      const overlay = await fetchOverlay(url);
      if (!overlay) {
        window.location.href = url;
        return;
      }
      content.innerHTML = "";
      content.appendChild(overlay);
      const title = overlay.querySelector('.c-production__title');
      if (title && !title.id) {
        title.id = 'production-modal-title';
      }
      initProductionTabs();
      initProductionOverlayImages(content);
      initBlurUp(content);
      initProjectTitlePan(content);
      destroyCarousel = initProductionCarousel(content);
      setModalState(modal, true);
      // Focus management for accessibility
      const firstFocusable = content.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    } catch (error) {
      window.location.href = url;
    }
  };

  document.querySelectorAll(".c-project__trigger[data-url]").forEach((trigger) => {
    trigger.addEventListener("click", () => {
      const url = trigger.getAttribute("data-url");
      if (url) {
        openModal(url);
      }
    });
  });

  bindModalEvents(modal, openModal);
};
